import React, { useEffect, useState, useContext, useCallback, useMemo, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, FileText, Users, Search, GitBranch, MessageSquare, Settings, Bell, ChevronsLeft, ChevronsRight } from 'lucide-react';

import { useCompany } from '../../contexts/CompanyContext';
import { AuthContext } from '../../contexts/AuthContext';
import { NavigationGuardProvider, useNavigationGuard } from '../../contexts/NavigationGuardContext';
import useCompanyJourneyFlag from '../../hooks/useCompanyJourneyFlag';
import useCompanyJourney from '../../hooks/useCompanyJourney';
import useCompanyProgressFlag from '../../hooks/useCompanyProgressFlag';
import useCompanyProgress from '../../hooks/useCompanyProgress';
import UnsavedChangesModal from '../UnsavedChangesModal';
import UnderReviewModal from '../GroupIntake/UnderReviewModal';
import { getBackendUrl } from '../../utils/apiUrl';
import { getExistingAnonymousUserId, clearAnonymousUserId } from '../../utils/anonymousUser';
import { clearAnonymousSwipes, getAnonymousSwipes } from '../../utils/anonymousSwipeStorage';
import negotiationLink from '../../utils/negotiationLink';
import { syncSubscription } from '../../api/billingApi';
import AnonymousSwipeClaimModal from '../ShareLink/AnonymousSwipeClaimModal';
import { toast } from 'react-toastify';
import { AVAILABILITY_SEARCH_ENABLED } from '../../config/availabilitySearch';
import { PARTNER_CONNECTIONS_ENABLED } from '../../config/partnerConnections';
import '../../styles/company-design-system.css';
import './CompanyLayout.css';

const BACKEND_URL = getBackendUrl();

// `countKey` names which live counter feeds this item's badge. NAV_ITEMS is a
// module-scope constant so it cannot hold the count itself; the component
// resolves the key against `navCounts` on every render.
const NAV_ITEMS = [
    { path: '/company/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/company/listings', label: 'Listings', icon: FileText },
    { path: '/company/providers', label: 'Coverage', icon: Users },
    // Anesthesia Coverage Search. The full name does not fit the sidebar.
    ...(AVAILABILITY_SEARCH_ENABLED ? [{ path: '/company/find-providers', label: 'Coverage Search', icon: Search }] : []),
    { path: '/company/pipeline', label: 'Pipeline', icon: GitBranch, countKey: 'pipeline' },
    { path: '/company/settings', label: 'Settings', icon: Settings },
    ...(PARTNER_CONNECTIONS_ENABLED ? [{ path: '/company/coverage-integrations', label: 'Connections', icon: Settings }] : []),
];

const navCountLabel = (label, count) => (count > 0 ? `${label}, ${count} needing action` : label);

// Where the claim modal's "post a listing" CTA sends the recruiter. Named
// because it is used twice and the two uses MUST agree: once to navigate, once
// to recognise the resulting pathname and hold the modal back.
const LISTINGS_PATH = '/company/listings';

// The same page, opened straight into the manual listing builder rather than
// the roster. `?new=1` is CompanyListingsPage's existing door into that view
// (it reads the param during render and hands it to CompanyListingsSurface,
// which opens the card editor on a null listing), so this is an address that
// already exists rather than a new one.
//
// DERIVED from LISTINGS_PATH, and LISTINGS_PATH deliberately left alone: the
// suppression check above compares against `location.pathname`, which never
// carries a query string. Folding `?new=1` into the constant itself would make
// that comparison fail and re-open the modal on top of the builder it just
// sent the recruiter to.
const LISTINGS_NEW_URL = `${LISTINGS_PATH}?new=1`;

// The two paths progressive disclosure can withhold. Named for the same reason
// LISTINGS_PATH is: the filter below and NAV_ITEMS above MUST agree.
const PROVIDERS_PATH = '/company/providers';
const PIPELINE_PATH = '/company/pipeline';

// The board's own answer for one milestone. A milestone we hold no row for
// reads as unknown, which shows, because an absent answer is not a zero.
const milestoneState = (chapters, id) => {
    for (const chapter of chapters || []) {
        for (const item of chapter.milestones || []) {
            if (item.id === id) return item.state;
        }
    }
    return 'unknown';
};

const CompanyLayout = ({ children }) => (
    <NavigationGuardProvider>
        <CompanyLayoutInner>{children}</CompanyLayoutInner>
    </NavigationGuardProvider>
);

const CompanyLayoutInner = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { auth } = useContext(AuthContext);
    const {
        company,
        user,
        notificationCount,
        // Still read: the topbar's "N Active Listings" badge is an independent
        // consumer. `activeListingsCountLoaded` is back with D1.12.7, which
        // shows a brand-new account its own 0 and therefore has to tell a
        // genuine zero from an unfetched one.
        activeListingsCount,
        activeListingsCountLoaded,
    } = useCompany();

    // ── Sidebar collapse state (persisted) ──
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try { return localStorage.getItem('cv-sidebar-collapsed') === 'true'; } catch { return false; }
    });
    const toggleSidebar = useCallback(() => {
        setSidebarCollapsed(prev => {
            const next = !prev;
            try { localStorage.setItem('cv-sidebar-collapsed', String(next)); } catch {}
            return next;
        });
    }, []);

    // ── Navigation guard state ──
    const { getActiveGuard } = useNavigationGuard();
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [pendingNavPath, setPendingNavPath] = useState(null);

    // ── Anonymous swipe claim state ──
    // `pendingSwipes` is fetched HERE and handed to the modal as a prop, so the
    // modal never re-POSTs claim-anonymous itself.
    const [showAnonymousSwipeModal, setShowAnonymousSwipeModal] = useState(false);
    const [pendingSwipes, setPendingSwipes] = useState([]);
    const [focusInterestId, setFocusInterestId] = useState(null);

    const claimAuthToken = useMemo(() => {
        if (auth?.token) return auth.token;
        try { return localStorage.getItem('authToken') || ''; } catch { return ''; }
    }, [auth?.token]);

    // The one hook every mount point (this shell, the dashboard board, the
    // priority-actions filter) reads company_journey through, so all three
    // stay in agreement instead of carrying separate copies of the same
    // subscription.
    const companyJourneyEnabled = useCompanyJourneyFlag();
    const progressBoardEnabled = useCompanyProgressFlag();
    const newBoardOn = companyJourneyEnabled && progressBoardEnabled;

    // Null token with the flag off, so a shell that is disclosing nothing makes
    // no request at all, and exactly one of the two payloads is ever requested.
    const journey = useCompanyJourney(
        companyJourneyEnabled && !newBoardOn ? claimAuthToken : null,
    );
    // The same hook the dashboard reads, so the sidebar and the board cannot
    // disagree about whether this account has a live requisition. React Query
    // dedupes the two mounts onto one request.
    const progress = useCompanyProgress(newBoardOn ? claimAuthToken : null);

    // One claim call at a time. The trigger effect below re-runs on every
    // company route change, so without this a burst of navigations would fire
    // concurrent POSTs against the same anonymous id.
    const claimInFlightRef = useRef(false);
    // Token this browser has already been told "nothing is waiting" for. Stops
    // a request per navigation once the durable record is empty; a new token
    // (fresh login) re-arms it.
    const claimExhaustedForTokenRef = useRef(null);
    const [claimAttempt, setClaimAttempt] = useState(0);
    const claimRefreshPendingRef = useRef(false);
    const claimMountedRef = useRef(true);
    // The one pathname the claim modal must NOT auto-open on, because the
    // modal's own CTA is what sent the recruiter there. See
    // `handleClaimModalGoToListings` below for why this is needed at all.
    // Null means "nothing suppressed", which is the normal state.
    const suppressClaimAutoOpenForPathRef = useRef(null);
    // Re-armed in SETUP, not just cleared in cleanup. StrictMode mounts, runs
    // effects, runs cleanups, then re-mounts in development, so a cleanup-only
    // flag ends that sequence permanently false and every guarded response is
    // discarded. Declared ABOVE the claim effect so the re-arm runs first on
    // the second mount.
    useEffect(() => {
        claimMountedRef.current = true;
        return () => { claimMountedRef.current = false; };
    }, []);

    // An in-app service search can save new interest after this layout already
    // learned that the queue was empty. Re-arm it without requiring a logout
    // or full reload; if a claim is in flight, repeat after it finishes.
    useEffect(() => {
        const refreshClaim = () => {
            claimExhaustedForTokenRef.current = null;
            if (claimInFlightRef.current) claimRefreshPendingRef.current = true;
            else setClaimAttempt(value => value + 1);
        };
        window.addEventListener('rxrooster:recruiterInterestStored', refreshClaim);
        return () => window.removeEventListener('rxrooster:recruiterInterestStored', refreshClaim);
    }, []);

    // ── Offer any pending recruiter interest, on every company route change ──
    // CompanyLayout is the route element for the whole /company/* subtree, so it
    // does not remount on in-app navigation. Keying this on pathname as well as
    // the token is what makes a skipped modal re-offer later instead of waiting
    // for a hard page load.
    useEffect(() => {
        const token = claimAuthToken;
        // Pending new outreach must never interrupt an existing conversation.
        // Keep reading the queue for the badge; only suppress its automatic UI.
        const readingConversation = /^\/company\/pipeline\/[^/]+/.test(location.pathname);
        const completingSecurePublication =
            location.pathname === '/company/mcp-publication';
        const suppressClaimInterruption =
            readingConversation || completingSecurePublication;
        if (suppressClaimInterruption) setShowAnonymousSwipeModal(false);
        if (!token) return;
        if (claimExhaustedForTokenRef.current === token) return;
        if (claimInFlightRef.current) return;

        claimInFlightRef.current = true;

        const offerPendingInterest = async () => {
            try {
                const localSwipeIdsAtStart = new Set(getAnonymousSwipes().map(swipe => swipe.id));
                let anonymousUserId = getExistingAnonymousUserId();

                if (!anonymousUserId) {
                    // This browser lost its anonymous id (cleared after an
                    // earlier claim, or the account was opened elsewhere). The
                    // server still knows which ids are linked to the account.
                    const checkRes = await fetch(`${BACKEND_URL}/swipes/check-anonymous`, {
                        method: 'GET',
                        headers: { 'Authorization': `Bearer ${token}` },
                    });
                    if (checkRes.ok) {
                        const checkData = await checkRes.json();
                        anonymousUserId = checkData?.anonymousUserId || null;
                    }
                }

                // A missing anonymous id is NOT a reason to skip the claim.
                // The id only BINDS newly-arrived anonymous swipes to the
                // account; the pending set itself comes from the durable
                // RecruiterInterest record, which outlives both the
                // localStorage id and the 24h AnonymousSwipe TTL. Gating the
                // request on the id is what left the pipeline's "Send a
                // listing" button dead on a second device, in a cleared
                // browser, or a day later: the row rendered from the durable
                // record while this layout still held an empty pendingSwipes,
                // so the ?interest= effect below had nothing to open on.
                const claimBody = anonymousUserId ? { anonymousUserId } : {};

                // Idempotent: binds any new anonymous swipes to the account and
                // returns the pending set from the durable RecruiterInterest
                // record, not from localStorage.
                const claimRes = await fetch(`${BACKEND_URL}/swipes/claim-anonymous`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(claimBody),
                });
                if (!claimRes.ok) return;

                const claimData = await claimRes.json();
                const swipes = Array.isArray(claimData?.pendingSwipes) ? claimData.pendingSwipes : [];
                if (!claimMountedRef.current) return;

                setPendingSwipes(swipes);

                if (swipes.length > 0) {
                    // True while the recruiter is standing on the page this
                    // modal's own CTA sent them to. Re-opening there would drop
                    // the modal on top of the task it just asked them to do.
                    //
                    // Gates the MODAL only, and only after setPendingSwipes
                    // above: `pendingSwipes` feeds the Pipeline nav badge, so
                    // suppressing the request or its result would blank a count
                    // that is still true.
                    const sentHereByTheModal =
                        suppressClaimAutoOpenForPathRef.current === location.pathname;

                    if (!sentHereByTheModal && !suppressClaimInterruption) {
                        suppressClaimAutoOpenForPathRef.current = null;
                        setShowAnonymousSwipeModal(true);
                    }
                } else {
                    // Authoritative "nothing waiting". Stop asking on every
                    // navigation and drop the local anonymous data that keyed it.
                    claimExhaustedForTokenRef.current = token;
                    // A new search may have created another anonymous id while
                    // this request was reading the old empty queue.
                    if (!claimRefreshPendingRef.current &&
                        getExistingAnonymousUserId() === anonymousUserId &&
                        getAnonymousSwipes().every(swipe => localSwipeIdsAtStart.has(swipe.id))) {
                        clearAnonymousUserId();
                        clearAnonymousSwipes();
                    }
                }
            } catch {
                // Network / parse failure. Leave the exhausted flag unset so the
                // next company route change retries.
            } finally {
                claimInFlightRef.current = false;
                if (claimRefreshPendingRef.current && claimMountedRef.current) {
                    claimRefreshPendingRef.current = false;
                    claimExhaustedForTokenRef.current = null;
                    setClaimAttempt(value => value + 1);
                }
            }
        };

        offerPendingInterest();
    }, [claimAuthToken, location.pathname, claimAttempt]);

    // ── ?interest=<interestId> deep link ──
    // The pipeline page's "Send a listing" CTA navigates to its own pathname
    // with this param. It is the only coupling between that page and this
    // layout.
    const interestParam = useMemo(
        () => new URLSearchParams(location.search || '').get('interest'),
        [location.search],
    );

    useEffect(() => {
        if (!interestParam) return;
        setFocusInterestId(interestParam);
        // Wait for the claim response before showing. The modal closes itself
        // on an empty queue, so opening it early would fire onHide, strip the
        // param and lose the focus before the data ever lands.
        //
        // This DEFERS the deep link, it does not swallow it: `pendingSwipes.length`
        // is a dependency, so the effect re-runs the moment the claim lands and
        // opens the modal then. `focusInterestId` is set above the guard, so the
        // id survives the wait. The one way this could strand a legitimate deep
        // link is the claim never being made at all, which is why the effect
        // above no longer gates that request on the anonymous id.
        //
        // Entries need no `anonymousSwipeId` to reach this path: nothing here
        // (or in the props handed to the modal) reads that field. The stable
        // identifier is `interestId`, which every entry carries.
        if (pendingSwipes.length === 0) return;
        setShowAnonymousSwipeModal(true);
    }, [interestParam, pendingSwipes.length]);

    // Drop ?interest= without pushing a history entry.
    const clearInterestParam = useCallback(() => {
        const params = new URLSearchParams(location.search || '');
        if (!params.has('interest')) return;
        params.delete('interest');
        const search = params.toString();
        navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    }, [location.search, location.pathname, navigate]);

    const handleClaimModalClose = useCallback(() => {
        setShowAnonymousSwipeModal(false);
        setFocusInterestId(null);
        clearInterestParam();
    }, [clearInterestParam]);

    // The claim modal's none-match panels all end at one button: "Post a
    // matching listing" / "Post a listing" / "Go to my listings". The modal
    // reports it here instead of navigating itself, because only this layout
    // can stop what happens next.
    //
    // What used to happen: the modal called navigate('/company/listings'),
    // which is a company route change, which re-fires the claim effect above,
    // which re-opened the modal on top of the listings page. The recruiter saw
    // the modal "reload". A second click then looked like it worked, but only
    // because navigating to the pathname you are already on leaves
    // `location.pathname` unchanged, so the effect never re-ran.
    //
    // Arming the ref BEFORE navigating is what makes this work: the effect
    // reads it when the claim response lands, which is always after this
    // synchronous handler has finished.
    //
    // Scoped to one pathname, not to the session: navigating anywhere else
    // clears it, so a skipped interest is still re-offered on the next route
    // change (CompanyLayout.jsx:102-106, and the two tests that pin it).
    // `intent` says which of the two doors the recruiter took. 'new' means the
    // CTA promised them a listing they do not have yet ("Post a matching
    // listing" / "Post a listing"), so dropping them on the roster makes them
    // hunt for the New listing button to do the thing they just asked for;
    // send them into the builder. Everything else ("Go to my listings", where
    // the job is to publish a listing they already have) belongs on the roster.
    //
    // Defaults to the roster on a bare call, so a caller that passes nothing
    // keeps the old behaviour rather than being surprised into the builder.
    const handleClaimModalGoToListings = useCallback((opts) => {
        // Armed with the PATHNAME, not the URL. See LISTINGS_NEW_URL.
        suppressClaimAutoOpenForPathRef.current = LISTINGS_PATH;
        handleClaimModalClose();
        navigate(opts?.intent === 'new' ? LISTINGS_NEW_URL : LISTINGS_PATH);
    }, [handleClaimModalClose, navigate]);

    // The recruiter dismissed one interest from the modal. The server has
    // already stamped `dismissedAt` by the time this runs, so the row is gone
    // from the next claim response and from the pipeline's Interested column.
    //
    // FILTERING `pendingSwipes` HERE IS NOT the thing the comment on navCounts
    // below forbids. That rule is about never deriving the badge from a
    // narrower VIEW of the queue than the modal offers, which is how the two
    // fell out of step before. This removes a row the server has confirmed
    // dismissed, from the one piece of state that feeds both -- so the modal
    // and the badge stay in agreement, which is the rule's actual point.
    const handleClaimModalInterestDismissed = useCallback((interestId, detail) => {
        if (!interestId) return;
        setPendingSwipes((prev) =>
            (Array.isArray(prev) ? prev : []).filter((entry) => entry?.interestId !== interestId),
        );
        // A heart on one service of a services page is its own interest, so
        // dismissing it drops that service, not the provider.
        toast.success(
            detail?.serviceId
                ? "Dismissed. We won't offer that service again."
                : "Dismissed. We won't offer that provider again.",
            { position: 'top-center', autoClose: 3000 },
        );
    }, []);

    // "Open the conversation". The company is already talking to this
    // provider, so the interest was a duplicate, and the modal has dismissed it
    // on the server before reporting here. Three things only this layout can
    // do, all at once: drop the row from `pendingSwipes` (the queue and the
    // Pipeline badge), close the modal, and navigate.
    //
    // The navigation is a company route change, so it re-fires the claim
    // effect above. Arming the suppression with the conversation's own path
    // first is what stops the modal re-opening on top of the conversation it
    // just opened, exactly as handleClaimModalGoToListings does for the
    // listings page. Any other interest still pending is offered again on the
    // next route change after that.
    const handleClaimModalOpenConversation = useCallback((conversationId, detail) => {
        const path = negotiationLink([{ conversationId }]);
        const interestId = detail?.interestId;
        if (interestId) {
            setPendingSwipes((prev) =>
                (Array.isArray(prev) ? prev : []).filter((entry) => entry?.interestId !== interestId),
            );
        }
        suppressClaimAutoOpenForPathRef.current = path;
        handleClaimModalClose();
        navigate(path);
        toast.success('We cleared the duplicate interest from your pipeline.', {
            position: 'top-center',
            autoClose: 3000,
        });
    }, [handleClaimModalClose, navigate]);

    const handleClaimComplete = useCallback(() => {
        setShowAnonymousSwipeModal(false);
        setFocusInterestId(null);
        setPendingSwipes([]);
        // Both localStorage keys: the anonymous user id AND the swipe payload.
        clearAnonymousUserId();
        clearAnonymousSwipes();
        clearInterestParam();
        toast.success("Listing sent. You'll hear back if they're interested.", { position: 'top-center', autoClose: 3000 });
    }, [clearInterestParam]);

    // Pipeline items needing action, from the claim response already in hand —
    // no extra request, and deliberately no filtering. Every entry in
    // `pendingSwipes` is one un-converted RecruiterInterest row, which is the
    // same set the pipeline's Interested column merges in, so the badge and
    // that column agree by construction. Filtering here (on
    // `matchingListings.length`, say, or on `anonymousSwipeId` being present)
    // would silently re-open the skew this repair closes.
    const navCounts = useMemo(() => ({ pipeline: pendingSwipes.length }), [pendingSwipes.length]);

    // ── Handle URL params: creditsSuccess + subscription sync ──
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const creditsSuccess = params.get('creditsSuccess');
        const viewParam = params.get('view');
        const sectionParam = params.get('section');

        if (creditsSuccess) {
            toast.success('Credits purchased successfully!', { position: 'top-center', autoClose: 3000 });
            window.history.replaceState({}, document.title, window.location.pathname);
            if (!viewParam) navigate('/company/settings?section=credits', { replace: true });
        }

        if (sectionParam === 'subscription') {
            const syncAndNotify = async () => {
                try {
                    const result = await syncSubscription();
                    toast.success(result?.synced ? `🎉 ${result.message}` : '🎉 Your subscription has been updated!', {
                        position: 'top-center', autoClose: 5000,
                    });
                } catch {
                    toast.success('🎉 Your subscription has been updated!', { position: 'top-center', autoClose: 5000 });
                }
            };
            syncAndNotify();
        }
    }, [navigate]);

    // Greeting helper
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 18) return 'Good afternoon';
        return 'Good evening';
    };

    // D1.12.7. State 1 is the only moment the org's own name is more useful than
    // the hour of the day: the account is brand new and the greeting is
    // confirming which organization it landed in. Every state after that, the
    // time greeting is the warmer line.
    const topbarGreeting =
        newBoardOn && progress.progress?.state === 1 ? 'Welcome' : getGreeting();

    const fallbackUser = user || auth?.user;
    const companyName = company?.name || fallbackUser?.group?.name || fallbackUser?.firstName || 'Company';
    const initials = `${(fallbackUser?.firstName || 'C')[0]}${(fallbackUser?.lastName || '')[0] || ''}`.toUpperCase();

    // D1.12.7. A brand-new account is told it has 0 active listings rather than
    // shown nothing, because nothing reads as "we did not check". Only in state
    // 1, and only once the count itself actually landed: an unloaded count is
    // not a zero.
    const showZeroListingCount =
        newBoardOn &&
        progress.progress?.state === 1 &&
        activeListingsCountLoaded &&
        (activeListingsCount || 0) === 0;

    // Scroll shadow on top bar
    useEffect(() => {
        const content = document.querySelector('.cv-content-area');
        if (!content) return;
        const handleScroll = () => {
            const topbar = document.querySelector('.cv-topbar');
            if (topbar) {
                topbar.classList.toggle('cv-topbar--scrolled', content.scrollTop > 0);
            }
        };
        content.addEventListener('scroll', handleScroll);
        return () => content.removeEventListener('scroll', handleScroll);
    }, []);

    // ── Navigation guard handler ──
    const handleNavClick = useCallback((e, path) => {
        // If already on that path, let it through
        if (location.pathname === path || location.pathname.startsWith(path + '/')) return;

        const guard = getActiveGuard();
        if (guard) {
            e.preventDefault();
            setPendingNavPath(path);
            setShowUnsavedModal(true);
        }
        // Otherwise, NavLink default behavior handles navigation
    }, [location.pathname, getActiveGuard]);

    const handleSaveAndNavigate = useCallback(async () => {
        const guard = getActiveGuard();
        if (guard?.onSave) {
            try {
                await guard.onSave();
            } catch (err) {
                console.error('Failed to save:', err);
                toast.error('Failed to save changes. Please try again.', { autoClose: 3000 });
                setShowUnsavedModal(false);
                setPendingNavPath(null);
                return;
            }
        }
        setShowUnsavedModal(false);
        if (pendingNavPath) {
            navigate(pendingNavPath);
            setPendingNavPath(null);
        }
    }, [getActiveGuard, pendingNavPath, navigate]);

    const handleDiscardAndNavigate = useCallback(() => {
        const guard = getActiveGuard();
        if (guard?.onDiscard) {
            guard.onDiscard();
        }
        setShowUnsavedModal(false);
        if (pendingNavPath) {
            navigate(pendingNavPath);
            setPendingNavPath(null);
        }
    }, [getActiveGuard, pendingNavPath, navigate]);

    const handleCancelNavigation = useCallback(() => {
        setShowUnsavedModal(false);
        setPendingNavPath(null);
    }, []);

    // Only a loaded, definite zero hides a surface. 'unmet' is the one answer
    // that withholds; 'unknown', a failed read and a journey we have not
    // fetched yet all show everything, because a flaky read must never lock a
    // recruiter out of a page they can already use.
    //
    // The journey payload is the ONLY input for Providers/Pipeline.
    // activeListingsCount counts reviewed listings straight off /listings and
    // does not require the facility resolution first-crow does, so the two
    // disagree by construction; letting it vote here would override the answer
    // that knows more. It stays destructured above for the topbar badge and
    // nothing else.
    //
    // THE MARKET REPORT GATE THAT USED TO LIVE HERE IS GONE WITH ITS TAB. The
    // report is not a destination any more: its portfolio is the market columns
    // on /company/listings, and the per-listing deep dive is reached from a row.
    // A gate deciding whether to show a tab that no longer exists would be dead
    // code that still had to be reasoned about on every future edit here.
    //
    // Computed once and read by BOTH the desktop sidebar and the mobile
    // tabbar below, matching NAV_ITEMS' own "used twice, must agree" rule.
    const visibleNavItems = useMemo(() => {
        // NAV IS A SERVER ANSWER NOW, not a milestone the shell re-derives.
        // `nav.showProviders` and `nav.showPipeline` come off the same
        // evaluation that decides the milestone statuses, so the sidebar and
        // the board cannot disagree; `pendingSwipes` still votes for Pipeline
        // because a waiting claim is a reason to reach it that the server read
        // could not have seen.
        if (newBoardOn) {
            const nav = progress.progress?.nav;
            return NAV_ITEMS.filter(({ path }) => {
                if (!progress.known || !nav) return true;
                if (path === PROVIDERS_PATH) return !!nav.showProviders;
                if (path === PIPELINE_PATH)
                    return !!nav.showPipeline || pendingSwipes.length > 0;
                return true;
            });
        }
        const hasLiveListing =
            companyJourneyEnabled && journey.known
                ? milestoneState(journey.chapters, 'first-crow') !== 'unmet'
                : null;
        const hasAnyInterest =
            companyJourneyEnabled && journey.known
                ? milestoneState(journey.chapters, 'first-interest') !== 'unmet' ||
                  pendingSwipes.length > 0
                : null;
        return NAV_ITEMS.filter(({ path }) => {
            if (!companyJourneyEnabled || !journey.known) return true;
            if (path === PROVIDERS_PATH) return hasLiveListing;
            if (path === PIPELINE_PATH) return hasLiveListing || hasAnyInterest;
            return true;
        });
    }, [
        newBoardOn,
        progress.known,
        progress.progress,
        companyJourneyEnabled,
        journey.known,
        journey.chapters,
        pendingSwipes.length,
    ]);

    const navItems = visibleNavItems.map(({ path, label, icon: Icon, countKey }) => {
        const count = countKey ? (navCounts[countKey] || 0) : 0;
        return (
            <NavLink
                key={path}
                to={path}
                onClick={(e) => handleNavClick(e, path)}
                className={({ isActive }) =>
                    `cv-nav__item ${isActive || location.pathname.startsWith(path) ? 'cv-nav__item--active' : ''}`
                }
                aria-label={navCountLabel(label, count)}
                title={sidebarCollapsed ? label : undefined}
            >
                <Icon size={20} strokeWidth={1.8} />
                <span className="cv-nav__label">{label}</span>
                {count > 0 && (
                    <span
                        className="cv-nav__count"
                        data-testid={`cv-nav-count-${countKey}`}
                        aria-hidden="true"
                    >
                        {count > 9 ? '9+' : count}
                    </span>
                )}
            </NavLink>
        );
    });

    return (
        <div className="cv-app">
            {/* ─── Desktop Sidebar ─── */}
            <aside className={`cv-sidebar ${sidebarCollapsed ? 'cv-sidebar--collapsed' : ''}`} role="navigation" aria-label="Main navigation">
                <div className="cv-sidebar__header">
                    <div
                        className="cv-sidebar__brand"
                        onClick={() => navigate('/company/dashboard')}
                        onKeyDown={(e) => e.key === 'Enter' && navigate('/company/dashboard')}
                        role="button"
                        tabIndex={0}
                    >
                        <span className="cv-sidebar__logo">
                            <img src="/logo-rooster.png" alt="RxRooster" width={24} height={24} decoding="async" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                        </span>
                        <span className="cv-sidebar__name">RxRooster</span>
                    </div>
                    <button
                        className="cv-sidebar__toggle"
                        onClick={toggleSidebar}
                        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {sidebarCollapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
                    </button>
                </div>
                <nav className="cv-sidebar__nav">
                    {navItems}
                </nav>
                <div className="cv-sidebar__footer">
                    <button
                        className="cv-sidebar__avatar-btn"
                        onClick={() => navigate('/company/settings')}
                        aria-label="Company settings"
                        title={sidebarCollapsed ? companyName : undefined}
                    >
                        <span className="cv-sidebar__avatar-initials">{initials}</span>
                        <span className="cv-sidebar__avatar-name">{companyName}</span>
                    </button>
                </div>
            </aside>

            {/* ─── Main Content Wrapper ─── */}
            <div className="cv-content-area">
                {/* ─── Top Bar ─── */}
                <header className="cv-topbar" role="banner">
                    <div className="cv-topbar__left">
                        <button
                            className="cv-topbar__avatar"
                            onClick={() => navigate('/company/settings')}
                            aria-label="Company settings"
                        >
                            <span className="cv-topbar__avatar-initials">{initials}</span>
                        </button>
                        <span className="cv-topbar__greeting">
                            {topbarGreeting}, {companyName}
                        </span>
                    </div>
                    <div className="cv-topbar__right">
                        {(activeListingsCount > 0 || showZeroListingCount) && (
                            <button
                                className="cv-badge cv-badge--teal cv-topbar__metric"
                                onClick={() => navigate('/company/listings')}
                                aria-label={`${activeListingsCount} active listing${activeListingsCount !== 1 ? 's' : ''}. Tap to view.`}
                            >
                                {activeListingsCount} Active Listing{activeListingsCount !== 1 ? 's' : ''}
                            </button>
                        )}
                        <button
                            className="cv-topbar__bell"
                            onClick={() => navigate('/company/notifications')}
                            aria-label={`${notificationCount} unread notifications`}
                        >
                            <Bell size={22} strokeWidth={1.8} />
                            {notificationCount > 0 && <span className="cv-topbar__bell-badge" aria-hidden="true" />}
                        </button>
                    </div>
                </header>

                {/* THE pending_review BANNER IS GONE (D1.12.8). GroupApprovalGate
                    owns review status and says it in one place; this banner said
                    the same thing in a second place, off a field the gate does
                    not read, and the two could never be made to agree. */}

                {/* ─── Main Content ─── */}
                <main className={`cv-main${/^\/company\/(providers|coverage)(\/|$)/.test(location.pathname) ? " cv-main--coverage" : ""}`} role="main">
                    {children}
                </main>
            </div>

            {/* ─── Mobile Tab Bar ─── */}
            <nav className="cv-tabbar" role="navigation" aria-label="Main navigation">
                {visibleNavItems.map(({ path, label, icon: Icon, countKey }) => {
                    const count = countKey ? (navCounts[countKey] || 0) : 0;
                    return (
                        <NavLink
                            key={path}
                            to={path}
                            onClick={(e) => handleNavClick(e, path)}
                            className={({ isActive }) =>
                                `cv-tabbar__item ${isActive || location.pathname.startsWith(path) ? 'cv-tabbar__item--active' : ''}`
                            }
                            aria-label={navCountLabel(label, count)}
                        >
                            <Icon size={22} strokeWidth={1.8} />
                            <span className="cv-tabbar__label">{label}</span>
                            {count > 0 && (
                                <span
                                    className="cv-tabbar__count"
                                    data-testid={`cv-tabbar-count-${countKey}`}
                                    aria-hidden="true"
                                >
                                    {count > 9 ? '9+' : count}
                                </span>
                            )}
                        </NavLink>
                    );
                })}
            </nav>

            {/* ─── Anonymous Swipe Claim Modal ─── */}
            {showAnonymousSwipeModal && (
                <AnonymousSwipeClaimModal
                    show={showAnonymousSwipeModal}
                    onHide={handleClaimModalClose}
                    onClaimComplete={handleClaimComplete}
                    pendingSwipes={pendingSwipes}
                    authToken={claimAuthToken}
                    focusInterestId={focusInterestId}
                    onGoToListings={handleClaimModalGoToListings}
                    onInterestDismissed={handleClaimModalInterestDismissed}
                    onOpenConversation={handleClaimModalOpenConversation}
                />
            )}
            {/* ─── Unsaved Changes Modal ─── */}
            <UnsavedChangesModal
                show={showUnsavedModal}
                onSave={handleSaveAndNavigate}
                onDiscard={handleDiscardAndNavigate}
                onCancel={handleCancelNavigation}
            />
            {/* ─── Review Status Modal (global for all /company/* pages) ─── */}
            <UnderReviewModal />
        </div>
    );
};

export default CompanyLayout;
